import { waitForConnectionWithToast } from '../ws';
import { gql } from './client';
import { emit, withExpected, waitForEvent, postToS3, firstOrThrow, type PresignedPost } from './events';

export interface PolicyInputDocument {
  s3Key: string;
  filename: string;
  contentType: string;
  size: number;
  // Absent on documents uploaded before malware scanning existed, which are trusted.
  scanStatus?: 'Pending' | 'Clean' | 'Infected' | 'Failed';
  scanDetail?: string;
}

export type OutputStatus = 'NotGenerated' | 'Generating' | 'Generated' | 'Stale' | 'Failed';
export type PolicyScope = 'Global' | 'Tenant';

export interface PolicyInput {
  id: string;
  name: string;
  scope: PolicyScope;
  tenantId: string;
  jurisdiction?: string;
  documents: PolicyInputDocument[];
  output?: string;
  outputStatus: OutputStatus;
  outputGeneratedAt?: string;
  lastGenerationInputTokens?: number;
  lastGenerationOutputTokens?: number;
  lastGenerationCostUsd?: number;
  lastGenerationModel?: string;
  lastEditedBy?: string;
  supplementaryNotes?: string;
  createdAt: string;
  updatedAt: string;
  version?: string;
}

const FIELDS = `id name scope tenantId jurisdiction documents output outputStatus outputGeneratedAt
  lastGenerationInputTokens lastGenerationOutputTokens lastGenerationCostUsd lastGenerationModel
  lastEditedBy supplementaryNotes createdAt updatedAt version`;

const SUMMARY_FIELDS = `id name scope tenantId jurisdiction documents outputStatus outputGeneratedAt
  lastGenerationInputTokens lastGenerationOutputTokens lastGenerationCostUsd lastGenerationModel
  lastEditedBy createdAt updatedAt version`;

// List rows come from the light summary query: `output` and `supplementaryNotes` (tens of KB each)
// are left undefined here. Use getPolicyInput(id) when you need the full record.
export async function listPolicyInputs(): Promise<PolicyInput[]> {
  const data = await gql<{ elevationsPlanningPolicyInputSummaries: PolicyInput[] }>(
    `query ListPolicyInputs { elevationsPlanningPolicyInputSummaries { ${SUMMARY_FIELDS} } }`,
  );
  return (data.elevationsPlanningPolicyInputSummaries ?? []).map(p => ({ ...p, documents: p.documents ?? [] }));
}

export async function getPolicyInput(id: string): Promise<PolicyInput | undefined> {
  const data = await gql<{ elevationsPlanningPolicyInput: PolicyInput | null }>(
    `query GetPolicyInput($id: ID!) { elevationsPlanningPolicyInput(id: $id) { ${FIELDS} } }`,
    { id },
  );
  const p = data.elevationsPlanningPolicyInput;
  return p ? { ...p, documents: p.documents ?? [] } : undefined;
}

// The server assigns its own id; this throwaway entityId only correlates the event with its
// websocket notification, so the caller re-lists after completion to find the new record.
export function emitCreatePolicyInput(
  payload: { name: string; scope: PolicyScope; tenantId?: string; jurisdiction?: string; performedBy?: string },
  connectionId?: string,
): Promise<string> {
  return emit('CreatePolicyInput', crypto.randomUUID(), payload, connectionId);
}

export function emitUpdatePolicyInput(
  id: string,
  patch: {
    name?: string; jurisdiction?: string; output?: string; outputS3Key?: string;
    supplementaryNotes?: string; performedBy?: string;
  },
  connectionId?: string,
  expectedValues?: Record<string, unknown>,
): Promise<string> {
  return emit('UpdatePolicyInput', id, withExpected(patch, expectedValues), connectionId);
}

export function emitDeletePolicyInput(id: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('DeletePolicyInput', id, { performedBy }, connectionId);
}

export async function getPolicyInputUploadUrl(
  policyInputId: string, filename: string, contentType: string,
): Promise<PresignedPost> {
  const data = await gql<{ elevationsPlanningPolicyInputUploadUrl: PresignedPost[] }>(
    `query GetPolicyInputUploadUrl($policyInputId: ID!, $filename: String!, $contentType: String!) {
      elevationsPlanningPolicyInputUploadUrl(policyInputId: $policyInputId, filename: $filename, contentType: $contentType) { url fields s3Key }
    }`,
    { policyInputId, filename, contentType },
  );
  return firstOrThrow(data.elevationsPlanningPolicyInputUploadUrl, 'Failed to get an upload URL');
}

export async function getPolicyInputDownloadUrl(policyInputId: string, s3Key: string): Promise<string> {
  const data = await gql<{ elevationsPlanningPolicyInputDownloadUrl: { url: string }[] }>(
    `query GetPolicyInputDownloadUrl($policyInputId: ID!, $s3Key: String!) {
      elevationsPlanningPolicyInputDownloadUrl(policyInputId: $policyInputId, s3Key: $s3Key) { url }
    }`,
    { policyInputId, s3Key },
  );
  return firstOrThrow(data.elevationsPlanningPolicyInputDownloadUrl, 'Failed to get a download URL').url;
}

// The Event Service rejects emitEvent payloads over 64KB and a generated output can exceed that
// alone, so a large `output` save is staged in S3 and referenced by key.
export async function getPolicyInputOutputUploadUrl(policyInputId: string): Promise<PresignedPost> {
  const data = await gql<{ elevationsPlanningPolicyInputOutputUploadUrl: PresignedPost[] }>(
    `query GetPolicyInputOutputUploadUrl($policyInputId: ID!) {
      elevationsPlanningPolicyInputOutputUploadUrl(policyInputId: $policyInputId) { url fields s3Key }
    }`,
    { policyInputId },
  );
  return firstOrThrow(data.elevationsPlanningPolicyInputOutputUploadUrl, 'Failed to get an upload URL');
}

export function uploadPolicyInputDocumentToS3(url: string, fields: Record<string, string>, file: File): Promise<void> {
  return postToS3(url, fields, file);
}

export function uploadPolicyInputOutputToS3(url: string, fields: Record<string, string>, text: string): Promise<void> {
  return postToS3(url, fields, new Blob([text], { type: 'text/plain' }));
}

export function emitConfirmPolicyInputDocument(
  policyInputId: string, s3Key: string, filename: string, performedBy?: string, connectionId?: string,
): Promise<string> {
  return emit('ConfirmPolicyInputDocument', policyInputId, { s3Key, filename, performedBy }, connectionId);
}

export function emitDeletePolicyInputDocument(
  policyInputId: string, s3Key: string, performedBy?: string, connectionId?: string,
): Promise<string> {
  return emit('DeletePolicyInputDocument', policyInputId, { s3Key, performedBy }, connectionId);
}

// Only kicks generation off; the Bedrock work runs in the service's async worker. Resolving
// means the record now shows Generating, not that the output is ready.
export async function generatePolicyInputOutput(policyInputId: string, performedBy?: string): Promise<void> {
  const connId = await waitForConnectionWithToast();
  const eventId = await emit('GeneratePolicyInputOutputRequested', policyInputId, { performedBy }, connId ?? undefined);
  return waitForEvent(eventId, 30_000);
}
